import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSound from 'use-sound'

import PageWrapper from '../../components/PageWrapper'
import Loading from '../../components/Loading'
import useWebSocket from '../../hooks/useWebSocket'
import useAuthContext from '../../hooks/useAuthContext'
import useUserContext from '../../hooks/useUserContext'
import history, { closeGameSessionData } from '../../routes/history'
import utils from '../../utils/utils'
import { useFullCatalog } from './hooks/use-catalog-full'
import { useCatalogFilters } from './hooks/use-catalog-filters'
import {
  Main,
  BackgroundLayer,
  GlowOrb,
  Content,
  CatalogContent,
  CatalogBleed,
} from './styled'

import { AllCatalog } from './components/all-catalog'
import { GameContainer } from './components/game-container'
import { HistoryModal } from './components/history-modal'
import { SearchBar } from './components/top-bar'
import { TopGames } from './components/top-games'

import { useCatalogAllTop } from './hooks/use-catalog-all-top'
import plickSound from './assets/plick.mp3'

import './components/top-bar/categories-icons.scss'
import './components/game-container/game-container.scss'
import './components/game-info-popup/game-info-popup.scss'

const SLOTS_PATH = '/slots'

const getGameSlug = (game) => game.name.toLowerCase().replace(/\s/g, '-')

export const SlotsPage = () => {
  const { isAuthenticated } = useAuthContext()
  const { updateUserBalance } = useUserContext()
  const { types, games } = useFullCatalog()
  const {
    selectedType,
    searchText,
    sortBy,
    sortDirection,
    setSelectedType,
    setSearchText,
    handleSort,
  } = useCatalogFilters()

  const [catalogScrollElement, setCatalogScrollElement] = useState(null)

  const [showHistory, setShowHistory] = useState(false)

  const [gameSlug, setGameSlug] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('game') || ''
  })
  const [isDemo, setIsDemo] = useState(false)

  const [playPlick] = useSound(plickSound, { volume: 0.3 })

  const { favoriteGames, popularGames, recentGames, recomendedGames, isLoading } = useCatalogAllTop()

  const activeGame = useMemo(() => {
    if (!gameSlug || !games.length) {
      return null
    }

    return games.find((game) => getGameSlug(game) === gameSlug) || null
  }, [gameSlug, games])

  const websocketUrl = process.env.REACT_APP_PW_SOCKET_WAGERS
  const { readyState, webSocket } = useWebSocket(websocketUrl, {
    queryParams: { token: utils.getToken() },
  })

  const onBalanceMessage = useCallback((data) => {
    const { balance, currency } = data

    if (currency !== 'real') {
      return
    }

    const userBalance = utils.getUserBalance()
    updateUserBalance({ ...userBalance, balanceReal: balance })
  }, [updateUserBalance])

  useEffect(() => {
    if (readyState !== 1 || !webSocket || !isAuthenticated) {
      return undefined
    }

    const userId = utils.getUserInfo()?.id
    if (!userId) {
      return undefined
    }

    webSocket.emit('subscribe', userId)
    webSocket.on('wager:balance', onBalanceMessage)

    return () => {
      webSocket.off('wager:balance', onBalanceMessage)
    }
  }, [readyState, webSocket, onBalanceMessage, isAuthenticated])

  useEffect(() => {
    const params = new URLSearchParams(history.location.search)

    if (gameSlug) {
      params.set('game', gameSlug)
    } else {
      params.delete('game')
    }

    const search = params.toString()
    const nextSearch = search ? `?${search}` : ''

    if (history.location.search !== nextSearch) {
      history.replace({
        pathname: SLOTS_PATH,
        search: nextSearch,
      })
    }
  }, [gameSlug])

  const handleLaunchGame = useCallback((game, demo = false) => {
    playPlick()
    setIsDemo(demo)
    setGameSlug(getGameSlug(game))
  }, [playPlick])

  const closeGameHandler = useCallback((data) => {
    if (data?.type && data?.token_id) {
      closeGameSessionData(data)
    }

    setGameSlug('')
    setIsDemo(false)
  }, [])

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow || ''
      document.body.style.overflow = originalBodyOverflow || ''
    }
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchText('')
    setSelectedType('all')
  }, [setSearchText, setSelectedType])

  // Al cambiar de categoria
  const handleSelectType = useCallback((type) => {
    setSelectedType(type)
    setSearchText('')
  }, [setSelectedType, setSearchText])

  const showFeaturedSections =
    selectedType === 'all' && searchText.trim().length === 0

  return (
    <PageWrapper>
      <Main>
        <BackgroundLayer aria-hidden="true">
          <GlowOrb $top="-12%" $left="50%" $size="85%" $color="rgba(120, 188, 40, 0.22)" />
          <GlowOrb $top="8%" $left="78%" $size="55%" $color="rgba(33, 156, 38, 0.14)" />
          <GlowOrb $top="18%" $left="12%" $size="45%" $color="rgba(88, 130, 30, 0.12)" />
          <GlowOrb $top="62%" $left="82%" $size="50%" $color="rgba(74, 143, 26, 0.08)" />
          <GlowOrb $top="78%" $left="18%" $size="60%" $color="rgba(120, 188, 40, 0.07)" />
          <GlowOrb $top="45%" $left="50%" $size="70%" $color="rgba(15, 60, 10, 0.06)" />
        </BackgroundLayer>
        <Content>
          <SearchBar
            types={types}
            selectedType={selectedType}
            onSelectType={handleSelectType}
            searchText={searchText}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSearchChange={setSearchText}
            onClearSearch={handleClearSearch}
            onSort={handleSort}
            onOpenHistory={() => setShowHistory(true)}
          />

          <CatalogContent ref={setCatalogScrollElement}>
            <CatalogBleed>
            {showFeaturedSections ? (
              isLoading ? (
                <div className='loading-container'>
                  <Loading />
                </div>
              ) : (
              <>
                <TopGames title='Mis favoritos' games={favoriteGames} compactAspect onLaunchGame={handleLaunchGame} />
                <TopGames title='Recomendados para ti' games={recomendedGames} size='sm' compactAspect onLaunchGame={handleLaunchGame} />
                <TopGames title='Los mas jugados' games={popularGames} displayNumber featured='TOP mas jugados' onLaunchGame={handleLaunchGame} />
                <TopGames title='Juegos Nuevos' games={recentGames} compactAspect onLaunchGame={handleLaunchGame} />
              </>
              )
            ) : null}

            <AllCatalog
              selectedType={selectedType}
              searchText={searchText}
              sortBy={sortBy}
              sortDirection={sortDirection}
              scrollElement={catalogScrollElement}
              onLaunchGame={handleLaunchGame}
            />
            </CatalogBleed>
          </CatalogContent>
        </Content>
      </Main>

      <HistoryModal show={showHistory} onClose={() => setShowHistory(false)} />

      {Boolean(activeGame) && (
        <GameContainer
          game={activeGame}
          gameOpen={activeGame?.name}
          demo={isDemo}
          onGameClose={closeGameHandler}
        />
      )}
    </PageWrapper>
  )
}
